import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Building,
  Target,
  Users,
  TrendingUp,
  BarChart3,
  Settings,
  Brain,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const ProfileDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    identidade: null,
    avaliacoes: 0,
    resultados: 0,
    analytics: {}
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Buscar dados da identidade organizacional
      const identidadeResponse = await axios.get(`${backendUrl}/api/profile/identidade-organizacional`, {
        headers: { Authorization: `Bearer ${user?.token}` }
      });
      
      // Buscar estatísticas do fit cultural
      const statsResponse = await axios.get(`${backendUrl}/api/fit-cultural/dashboard-stats`, {
        headers: { Authorization: `Bearer ${user?.token}` }
      });

      setStats({
        identidade: identidadeResponse.data,
        ...statsResponse.data
      });
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      // Dados mockados para desenvolvimento
      setStats({
        identidade: null,
        avaliacoes: 0,
        resultados: 0,
        analytics: {}
      });
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, description, icon: Icon, color = "blue", action = null }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-gray-600">
          {title}
        </CardTitle>
        <Icon className={`h-4 w-4 text-${color}-600`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-gray-900 mb-1">{value}</div>
        <p className="text-xs text-gray-600 mb-3">{description}</p>
        {action && action}
      </CardContent>
    </Card>
  );

  const identidadeStatus = stats.identidade ? 'configured' : 'pending';
  const statusConfig = {
    configured: {
      color: 'green',
      icon: CheckCircle,
      text: 'Configurado',
      description: 'Identidade organizacional definida'
    },
    pending: {
      color: 'orange', 
      icon: AlertCircle,
      text: 'Pendente',
      description: 'Configure a identidade organizacional'
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-bold text-gray-900">
              🏛️ Profile - Identidade e Fit Cultural
            </h1>
            <Badge variant="secondary" className="text-sm">
              <Brain className="w-4 h-4 mr-1" />
              IA Integrada
            </Badge>
          </div>
          <p className="text-gray-600">
            Gerencie a identidade organizacional e analise o fit cultural com inteligência artificial
          </p>
        </div>

        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Identidade Organizacional"
            value={statusConfig[identidadeStatus].text}
            description={statusConfig[identidadeStatus].description}
            icon={statusConfig[identidadeStatus].icon}
            color={statusConfig[identidadeStatus].color}
            action={
              <Button 
                size="sm" 
                variant={identidadeStatus === 'configured' ? 'outline' : 'default'}
                className="w-full"
                onClick={() => window.location.href = '/profile/identidade-organizacional'}
              >
                {identidadeStatus === 'configured' ? 'Editar' : 'Configurar'}
              </Button>
            }
          />

          <StatCard
            title="Avaliações Realizadas"
            value={stats.avaliacoes || 0}
            description="Total de avaliações de fit cultural"
            icon={Target}
            color="blue"
            action={
              <Button size="sm" variant="outline" className="w-full">
                Ver Avaliações
              </Button>
            }
          />

          <StatCard
            title="Resultados Processados"
            value={stats.resultados || 0}
            description="Análises IA concluídas"
            icon={TrendingUp}
            color="green"
            action={
              <Button size="sm" variant="outline" className="w-full">
                Ver Resultados
              </Button>
            }
          />

          <StatCard
            title="Analytics Avançados"
            value="IA"
            description="Insights inteligentes disponíveis"
            icon={Brain}
            color="purple"
            action={
              <Button size="sm" variant="outline" className="w-full">
                Ver Analytics
              </Button>
            }
          />
        </div>

        {/* Navegação Rápida */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Acesso Rápido - Módulo Fit Cultural
            </CardTitle>
            <CardDescription>
              Navegue rapidamente pelas principais funcionalidades do módulo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <Button 
                variant="outline" 
                className="h-20 flex flex-col gap-2"
                onClick={() => window.location.href = '/profile/identidade-organizacional'}
              >
                <Building className="w-6 h-6" />
                <span className="text-xs text-center">Identidade Organizacional</span>
              </Button>

              <Button 
                variant="outline" 
                className="h-20 flex flex-col gap-2"
                onClick={() => window.location.href = '/profile/fit-cultural/valores'}
              >
                <Target className="w-6 h-6" />
                <span className="text-xs text-center">Gestão de Valores</span>
              </Button>

              <Button 
                variant="outline" 
                className="h-20 flex flex-col gap-2"
                onClick={() => window.location.href = '/profile/fit-cultural/avaliacoes'}
              >
                <Users className="w-6 h-6" />
                <span className="text-xs text-center">Configurar Avaliações</span>
              </Button>

              <Button 
                variant="outline" 
                className="h-20 flex flex-col gap-2"
                onClick={() => window.location.href = '/profile/fit-cultural/questionario'}
              >
                <FileText className="w-6 h-6" />
                <span className="text-xs text-center">Aplicar Questionário</span>
              </Button>

              <Button 
                variant="outline" 
                className="h-20 flex flex-col gap-2"
                onClick={() => window.location.href = '/profile/fit-cultural/analytics'}
              >
                <Brain className="w-6 h-6" />
                <span className="text-xs text-center">Análises IA</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Overview de Status */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="recent">Atividades Recentes</TabsTrigger>
            <TabsTrigger value="help">Ajuda & Tutorial</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Status do Sistema</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span>Identidade Organizacional</span>
                    <Badge variant={identidadeStatus === 'configured' ? 'success' : 'secondary'}>
                      {statusConfig[identidadeStatus].text}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Integração IA</span>
                    <Badge variant="success">Ativa</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Sistema de Avaliação</span>
                    <Badge variant="secondary">Em Desenvolvimento</Badge>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Próximos Passos</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {identidadeStatus === 'pending' && (
                    <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg">
                      <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-orange-900">Configure a Identidade</p>
                        <p className="text-sm text-orange-700">
                          Defina missão, visão e valores da empresa
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                    <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-900">Configurar Avaliações</p>
                      <p className="text-sm text-blue-700">
                        Crie questionários personalizados
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="recent" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Atividades Recentes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-500 text-center py-8">
                  Nenhuma atividade recente encontrada
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="help" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Guia de Início Rápido</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</div>
                    <div>
                      <h4 className="font-medium">Configure a Identidade Organizacional</h4>
                      <p className="text-sm text-gray-600">Defina missão, visão e valores da sua empresa</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</div>
                    <div>
                      <h4 className="font-medium">Crie Avaliações Personalizadas</h4>
                      <p className="text-sm text-gray-600">Configure questionários de acordo com seus valores</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</div>
                    <div>
                      <h4 className="font-medium">Aplique e Analise com IA</h4>
                      <p className="text-sm text-gray-600">Use inteligência artificial para insights profundos</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProfileDashboard;